<script setup>
import { ref, computed, watch, onUnmounted } from 'vue';

const props = defineProps({
  userLocation: {
    type: String,
    default: 'Kidderminster'
  }
});

// Reactive State
const locationName = ref('');
const formattedTime = ref('');
const formattedDate = ref('');
const timeZoneName = ref('');
const sunriseTime = ref('');
const sunsetTime = ref('');
const dayLength = ref('');
const dayProgress = ref(0);
const daylightRemaining = ref('');
const isDaylight = ref(true);
const loading = ref(false);
const errorMessage = ref('');

let clockInterval = null;
let currentTimeZone = null;
let rawSunrise = null;
let rawSunset = null;

// Clock Ticking & Progress Logic
const updateClock = () => {
  if (!currentTimeZone) return;

  const now = new Date();

  // 1. Time & Date
  formattedTime.value = new Intl.DateTimeFormat('en-GB', {
    timeZone: currentTimeZone,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).format(now);

  formattedDate.value = new Intl.DateTimeFormat('en-GB', {
    timeZone: currentTimeZone,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  }).format(now);

  // 2. Solar calculations
  if (rawSunrise && rawSunset) {
    const currentMs = now.getTime();
    const startMs = rawSunrise.getTime();
    const endMs = rawSunset.getTime();

    if (currentMs < startMs) {
      dayProgress.value = 0;
      isDaylight.value = false;
      daylightRemaining.value = 'Before Sunrise';
    } else if (currentMs > endMs) {
      dayProgress.value = 100;
      isDaylight.value = false;
      daylightRemaining.value = 'After Sunset';
    } else {
      isDaylight.value = true;
      const pct = ((currentMs - startMs) / (endMs - startMs)) * 100;
      dayProgress.value = Math.min(100, Math.max(0, Math.round(pct)));

      // Remaining daylight formatted
      const remainingMs = endMs - currentMs;
      const remHrs = Math.floor(remainingMs / (1000 * 60 * 60));
      const remMins = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));
      daylightRemaining.value = `${remHrs}h ${remMins}m left`;
    }
  }
};

// Trigonometry for SVG Sun Icon position along the arc
const sunIconCoords = computed(() => {
  // Arc angle from 180deg (left) to 0deg (right)
  const angleDeg = 180 - (dayProgress.value / 100) * 180;
  const angleRad = (angleDeg * Math.PI) / 180;
  
  // Radius = 75, Center = (100, 80)
  const x = 100 + 75 * Math.cos(angleRad);
  const y = 80 - 75 * Math.sin(angleRad);
  return { x, y };
});

const formatSunTime = (isoString, timeZone) => {
  if (!isoString) return '';
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(new Date(isoString));
};

const startClock = (timeZone) => {
  currentTimeZone = timeZone;
  if (clockInterval) clearInterval(clockInterval);
  updateClock();
  clockInterval = setInterval(updateClock, 1000);
};

// Fetch Data
const fetchTimeZone = async () => {
  const input = props.userLocation ? props.userLocation.trim() : '';
  if (!input) return;

  loading.value = true;
  errorMessage.value = '';

  try {
    let lat, lon, displayName;
    const isPostcode = /^[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2}$/i.test(input);

    if (isPostcode) {
      const pcRes = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(input)}`);
      const pcData = await pcRes.json();
      if (pcData.status !== 200) throw new Error('Invalid UK Postcode');
      lat = pcData.result.latitude;
      lon = pcData.result.longitude;
      displayName = `${pcData.result.outward_code}, ${pcData.result.admin_district}`;
    } else {
      const geoRes = await fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(input)}&count=5&language=en&format=json`);
      const geoData = await geoRes.json();
      if (!geoData.results || geoData.results.length === 0) throw new Error('Location not found');

      const ukMatch = geoData.results.find(place => place.country_code === 'GB') || geoData.results[0];
      lat = ukMatch.latitude;
      lon = ukMatch.longitude;
      displayName = `${ukMatch.name}${ukMatch.admin1 ? ', ' + ukMatch.admin1 : ''}`;
    }

    const tzRes = await fetch(
      `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&daily=sunrise,sunset,daylight_duration&timezone=auto`
    );
    if (!tzRes.ok) throw new Error('Failed to fetch time data');

    const tzData = await tzRes.json();
    if (!tzData.timezone) throw new Error('Timezone data unavailable');

    locationName.value = displayName;
    timeZoneName.value = tzData.timezone;

    if (tzData.daily && tzData.daily.sunrise?.length && tzData.daily.sunset?.length) {
      rawSunrise = new Date(tzData.daily.sunrise[0]);
      rawSunset = new Date(tzData.daily.sunset[0]);

      sunriseTime.value = formatSunTime(tzData.daily.sunrise[0], tzData.timezone);
      sunsetTime.value = formatSunTime(tzData.daily.sunset[0], tzData.timezone);

      if (tzData.daily.daylight_duration?.length) {
        const totalSecs = tzData.daily.daylight_duration[0];
        const hrs = Math.floor(totalSecs / 3600);
        const mins = Math.round((totalSecs % 3600) / 60);
        dayLength.value = `${hrs}h ${mins}m`;
      }
    }

    startClock(tzData.timezone);

  } catch (err) {
    if (clockInterval) clearInterval(clockInterval);
    errorMessage.value = err.message || 'Error fetching time data';
  } finally {
    loading.value = false;
  }
};

watch(() => props.userLocation, (newVal) => { if (newVal) fetchTimeZone(); }, { immediate: true });
onUnmounted(() => { if (clockInterval) clearInterval(clockInterval); });
</script>

<template>
  <div class="time-card">
    <div v-if="loading" class="state-message">Loading time...</div>
    <div v-else-if="errorMessage" class="error-message">{{ errorMessage }}</div>

    <template v-else>
      <!-- Header + Clock Section -->
      <div class="clock-section">
        <h2 class="title">Today</h2>
        <div class="time-display">
          <span 
            v-for="(char, index) in formattedTime.split('')" 
            :key="index"
            :class="{ 'digit': char !== ':', 'colon': char === ':' }"
          >
            {{ char }}
          </span>
        </div>
        <div class="date-display">{{ formattedDate }}</div>
      </div>

      <!-- Grounded Sun Tracker Box -->
      <div class="sun-tracker-box">
        <svg class="sun-arc-svg" viewBox="0 0 200 90" preserveAspectRatio="xMidYMin meet">
          <!-- Background Track Arc -->
          <path d="M 25 80 A 75 75 0 0 1 175 80" class="arc-bg" />
          
          <!-- Dashed Horizon Line -->
          <line x1="15" y1="80" x2="185" y2="80" class="horizon-line" />

          <!-- Dynamic Sun Dot -->
          <circle 
            :cx="sunIconCoords.x" 
            :cy="sunIconCoords.y" 
            r="6.5" 
            class="sun-dot" 
          />
        </svg>

        <div class="arc-labels">
          <div class="sun-time-item">
            <i class="bi bi-sunrise-fill icon"></i>
            <span>{{ sunriseTime }}</span>
          </div>
          <div v-if="isDaylight" class="daylight-badge">
            {{ daylightRemaining }}
          </div>
          <div class="sun-time-item">
            <i class="bi bi-sunset-fill icon"></i>
            <span>{{ sunsetTime }}</span>
          </div>
        </div>
      </div>

      <!-- Structured Footer Meta -->
      <div class="footer-meta">
        <span>Daylight: <strong>{{ dayLength }}</strong></span>
        <span class="dot">•</span>
        <span>{{ timeZoneName }}</span>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.time-card {
  background: #ffffff;
  padding: 1.5rem;
  border-radius: 16px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.08);
  width: 100%;
  height: 100%;
  box-sizing: border-box;

  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  font-family: system-ui, -apple-system, sans-serif;

  .state-message { color: #64748b; margin: auto 0; }
  .error-message { color: #e11d48; margin: auto 0; }

  /* Top Clock Block */
  .clock-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;

    .title {
      font-size: 1.15rem;
      color: #d97706;
      margin: 0 0 0.5rem 0;
      font-weight: 700;
    }

    .time-display {
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 3.1rem;
      font-weight: 700;
      color: #0f172a;
      letter-spacing: -1px;
      line-height: 1;
      margin: 0.15rem 0;

      .digit {
        width: 1.85rem;
        text-align: center;
        display: inline-block;
      }

      .colon {
        width: 0.75rem;
        text-align: center;
        display: inline-block;
      }
    }

    .date-display {
      font-size: 0.95rem;
      color: #831843;
      font-weight: 600;
      margin-top: 0.25rem;
    }
  }

  /* Grounded Box for Sun Tracker */
  .sun-tracker-box {
    width: 100%;
    background-color: #f8fafc;
    border: 1px solid #f1f5f9;
    border-radius: 12px;
    padding: 14px 16px 12px 16px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;

    .sun-arc-svg {
      width: 100%;
      max-width: 250px;
      height: 85px;
      overflow: visible;

      .arc-bg {
        fill: none;
        stroke: #fed7aa;
        stroke-width: 3.5;
        stroke-linecap: round;
      }

      .horizon-line {
        stroke: #e2e8f0;
        stroke-width: 1.5;
        stroke-dasharray: 4 4;
      }

      .sun-dot {
        fill: #ea580c;
        stroke: #ffffff;
        stroke-width: 2;
        filter: drop-shadow(0px 2px 4px rgba(234, 88, 12, 0.35));
        transition: cx 0.5s ease, cy 0.5s ease;
      }
    }

    .arc-labels {
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 2px;

      .sun-time-item {
        display: flex;
        align-items: center;
        gap: 5px;
        font-size: 0.8rem;
        font-weight: 700;
        color: #475569;

        .icon {
          color: #ea580c;
        }
      }

      .daylight-badge {
        background-color: #ffffff;
        border: 1px solid #ffedd5;
        color: #c2410c;
        font-size: 0.725rem;
        font-weight: 700;
        padding: 3px 10px;
        border-radius: 8px;
        box-shadow: 0 1px 2px rgba(0,0,0,0.03);
      }
    }
  }

  /* Footer Metadata Pill */
  .footer-meta {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background-color: #f8fafc;
    border: 1px solid #f1f5f9;
    padding: 4px 14px;
    border-radius: 12px;
    font-size: 0.75rem;
    color: #64748b;
    font-weight: 500;

    strong {
      color: #334155;
    }

    .dot {
      color: #cbd5e1;
    }
  }
}
</style>